import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, Collapse, Flex, FormControl, FormLabel, HStack, Input, Select, Spacer, Stack, Switch, Text, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiPlus } from 'react-icons/fi';
import IconWorkOrder from '../assets/Icon-WorkOrder.svg';
import DocumentGridShell from './DocumentGridShell';
import useDepartmentTasks, { DepartmentTask, DepartmentTeam, parseAssignees } from '../hooks/useDepartmentTasks';
import useUsers from '../hooks/useUsers';
import useCurrentUser from '../hooks/useCurrentUser';
import { AccessRole, UserDto } from '../models/user';
import TaskRow, { DEPARTMENT_LABELS, DEPARTMENT_ORDER } from './tasks/TaskRow';

// item 1466 (Chad: "go to Sales tasks under, say, Sunset Station, and be able to add tasks to
// that project… it would be cool if it showed up under the department tasks global section as
// well") — the project's TASKS section (Project Management bucket, between CUSTOMER and
// HAND-OFFS). Same task rows as the global board, filtered to THIS project and grouped under
// department subheaders; only departments with tasks here render. One Add box with a
// department picker that remembers your last choice (per device). "Mine only" hides everything
// not assigned to you. Done tasks fold into a collapsed group with Show more.
const LAST_DEPT_KEY = 'basix.tasks.lastDept';

const ProjectTasksSection: React.FC<{
  projectId: string;
  projectName?: string | null;
  customerName?: string | null;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({
  projectId, projectName, customerName, collapse,
}) => {
  const {
    tasks, status, addTask, setDone, archive, setAssignees, setRecurrence,
  } = useDepartmentTasks();
  const { data: usersData } = useUsers();
  const { user: me } = useCurrentUser();
  const myId: string | undefined = me?.id;
  const toast = useToast();
  // item 928 — externals can never be assignees
  const roster = (usersData?.index ?? []).filter((u: UserDto) => !u.isArchive
    && u.accessRole !== AccessRole.OutsideSales && u.accessRole !== AccessRole.CustomerBuilder);
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const headColor = useColorModeValue('gray.600', 'whiteAlpha.700');

  const [mineOnly, setMineOnly] = useState(false);
  const [draft, setDraft] = useState('');
  const [dept, setDept] = useState<DepartmentTeam>(() => {
    try {
      const v = window.localStorage.getItem(LAST_DEPT_KEY);
      return v == null || v === '' ? DepartmentTeam.Sales : (Number(v) as DepartmentTeam);
    } catch { return DepartmentTeam.Sales; }
  });
  const [doneOpen, setDoneOpen] = useState(false);
  const [doneShown, setDoneShown] = useState(15);

  const mine = (t: DepartmentTask) => !!myId && parseAssignees(t.assigneeUserIds).includes(myId);
  const projectTasks = useMemo(() => tasks.filter((t) => t.projectId === projectId), [tasks, projectId]);
  const visible = mineOnly ? projectTasks.filter(mine) : projectTasks;
  const open = visible.filter((t) => !t.isDone);
  const done = visible.filter((t) => t.isDone).slice().sort((a, b) => String(b.createdDate).localeCompare(String(a.createdDate)));
  const mineOpenCount = projectTasks.filter((t) => !t.isDone && mine(t)).length;
  const departmentsHere = DEPARTMENT_ORDER.filter((d) => open.some((t) => t.department === d));

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    try { window.localStorage.setItem(LAST_DEPT_KEY, String(dept)); } catch { /* best-effort */ }
    try {
      await addTask(dept, text, { id: projectId, name: projectName, customerName });
    } catch {
      setDraft(text);
      toast({ status: 'error', title: 'Could not add the task', description: 'It was NOT saved — try again.' });
    }
  };

  const rowProps = {
    users: roster,
    onToggle: (id: string, d: boolean) => setDone(id, d),
    onArchive: (id: string) => archive(id),
    onAssign: (id: string, ids: string[]) => setAssignees(id, ids),
    onRecurrence: setRecurrence,
  };

  return (
    <DocumentGridShell
      heading="TASKS"
      icon={IconWorkOrder}
      collapsible={!!collapse}
      collapsed={collapse?.collapsed}
      onToggleCollapse={collapse?.onToggle}
      status={status === 'loading' ? 'loading' : 'success'}
      data={projectTasks.filter((t) => !t.isDone)}
    >
      <Box>
        <Flex align="center" mb={3} flexWrap="wrap" sx={{ gap: '8px' }}>
          <Badge colorScheme="green" variant="subtle">{`${projectTasks.filter((t) => !t.isDone).length} open`}</Badge>
          <Badge variant="subtle">{`${projectTasks.filter((t) => t.isDone).length} done`}</Badge>
          {mineOpenCount > 0 && <Badge colorScheme="red" variant="subtle">{`${mineOpenCount} mine`}</Badge>}
          <Spacer />
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel htmlFor={`mine-only-${projectId}`} mb={0} fontSize="sm" color={subColor}>Mine only</FormLabel>
            <Switch id={`mine-only-${projectId}`} size="sm" colorScheme="red" isChecked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
          </FormControl>
        </Flex>

        <HStack mb={4}>
          <Input
            size="sm"
            placeholder="Add a task to this project…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          <Select size="sm" w="170px" value={dept} onChange={(e) => setDept(Number(e.target.value) as DepartmentTeam)} aria-label="Department">
            {DEPARTMENT_ORDER.map((d) => <option key={d} value={d}>{DEPARTMENT_LABELS[d]}</option>)}
          </Select>
          <Button size="sm" leftIcon={<FiPlus />} onClick={submit} isDisabled={!draft.trim()}>Add</Button>
        </HStack>

        {projectTasks.length === 0 && (
          <Text fontSize="sm" color={subColor}>No tasks on this project yet — add one above; it also shows on Tools › Department Tasks.</Text>
        )}
        {projectTasks.length > 0 && open.length === 0 && (
          <Text fontSize="sm" color={subColor}>{mineOnly ? 'Nothing open assigned to you on this project.' : 'Nothing open — everything here is done.'}</Text>
        )}

        <Stack spacing={4}>
          {departmentsHere.map((d) => {
            const rows = open.filter((t) => t.department === d);
            return (
              <Box key={d}>
                <HStack spacing={2} mb={2} pb={1} borderBottomWidth="1px">
                  <Text fontSize="xs" fontWeight="bold" letterSpacing="0.1em" color={headColor}>{DEPARTMENT_LABELS[d].toUpperCase()}</Text>
                  <Badge variant="subtle" fontSize="0.6rem">{rows.length}</Badge>
                </HStack>
                <Stack spacing={2}>
                  {rows.map((t) => <TaskRow key={t.id} task={t} mine={mine(t)} showMeta {...rowProps} />)}
                </Stack>
              </Box>
            );
          })}

          {done.length > 0 && (
            <Box>
              <Button size="xs" variant="ghost" color={subColor} leftIcon={doneOpen ? <FiChevronDown /> : <FiChevronRight />} onClick={() => setDoneOpen((o) => !o)}>
                {`Done (${done.length})`}
              </Button>
              <Collapse in={doneOpen} animateOpacity>
                <Stack spacing={2} mt={2}>
                  {done.slice(0, doneShown).map((t) => <TaskRow key={t.id} task={t} showDepartment showMeta {...rowProps} />)}
                  {done.length > doneShown && (
                    <Button size="xs" variant="ghost" alignSelf="flex-start" onClick={() => setDoneShown((n) => n + 15)}>
                      {`Show more (${done.length - doneShown} older)`}
                    </Button>
                  )}
                </Stack>
              </Collapse>
            </Box>
          )}
        </Stack>
      </Box>
    </DocumentGridShell>
  );
};

ProjectTasksSection.defaultProps = { projectName: undefined, customerName: undefined, collapse: undefined };

export default ProjectTasksSection;
